// frontend/src/utils/mockApi.js
// API simulada que responde con datos demo completos

import { 
  ADMIN_STATS, 
  PROYECTOS, 
  TAREAS, 
  USUARIOS_SISTEMA,
  INVITACIONES_JEFE,
  TAREAS_JEFE,
  PAGOS,
  PROYECTOS_COLABORADOR,
  SUSCRIPCION,
  TARIFAS,
  ACTIVIDAD_RECIENTE,
  NOTIFICACIONES,
  CONFIGURACION_EMPRESA
} from './mockData';
import { getDemoUser, getDemoToken } from './mockAuth';

// Simular delay de red
const delay = (ms = 300) => new Promise(resolve => setTimeout(resolve, ms));

// Verificar autenticación
function checkAuth() {
  const token = getDemoToken();
  if (!token) {
    const err = new Error('No hay sesión activa');
    err.status = 401;
    throw err;
  }
  return getDemoUser();
}

// Respuesta exitosa genérica
const successResponse = (message = 'Operación exitosa') => ({ 
  success: true, 
  message,
  demo: true 
});

function getProyectoDetalleMock(id) {
  const proyecto = PROYECTOS.find(p => p.id_proyecto === id) || PROYECTOS[0];
  const tareasProyecto = TAREAS.filter(t => t.id_proyecto === id);
  const colaboradores = USUARIOS_SISTEMA.filter(u => proyecto.colaboradores?.includes(u.id_usuario)).map(u => ({
    id_usuario: u.id_usuario,
    nombre: u.nombre,
    email: u.email,
    estado: u.estado || 'ACTIVO'
  }));

  const etapasMap = [
    { id_etapa: 1, nombre: 'Análisis y Levantamiento', estado: 'completada', orden: 1, slug: 'analisis', items: [] },
    { id_etapa: 2, nombre: 'Desarrollo e Implementación', estado: 'activa', orden: 2, slug: 'desarrollo', items: [] },
    { id_etapa: 3, nombre: 'Pruebas y QA', estado: 'pendiente', orden: 3, slug: 'pruebas', items: [] },
    { id_etapa: 4, nombre: 'Cierre y Despliegue', estado: 'pendiente', orden: 4, slug: 'cierre', items: [] }
  ];

  if (tareasProyecto.length > 0) {
    tareasProyecto.forEach((t, idx) => {
      const colabName = USUARIOS_SISTEMA.find(u => u.id_usuario === t.asignado_a)?.nombre || (colaboradores[0]?.nombre || 'Sin asignar');
      const eIdx = idx % etapasMap.length;
      etapasMap[eIdx].items.push({
        id_tarea: t.id_tarea || t.id,
        nombre: t.nombre,
        descripcion: t.descripcion,
        estado: t.estado === 'COMPLETADO' ? 'Completado' : (t.estado === 'EN_PROGRESO' ? 'En Progreso' : 'Pendiente'),
        prioridad: t.prioridad || 'MEDIA',
        fecha_inicio: t.fecha_creacion,
        fecha_termino: t.fecha_entrega,
        colaborador: colabName
      });
    });
  } else {
    etapasMap[0].items.push({
      id_tarea: 101,
      nombre: 'Levantamiento de Requerimientos',
      descripcion: 'Definición de alcance inicial',
      estado: 'Completado',
      prioridad: 'ALTA',
      fecha_inicio: proyecto.fecha_inicio,
      fecha_termino: proyecto.fecha_inicio,
      colaborador: colaboradores[0]?.nombre || 'María Fernández'
    });
    etapasMap[1].items.push({
      id_tarea: 102,
      nombre: 'Desarrollo de Módulo Principal',
      descripcion: 'Construcción de arquitectura base',
      estado: 'En Progreso',
      prioridad: 'ALTA',
      fecha_inicio: proyecto.fecha_inicio,
      fecha_termino: proyecto.fecha_fin,
      colaborador: colaboradores[1]?.nombre || 'Juan Pérez'
    });
  }

  const etapas = etapasMap.map(e => {
    const total = e.items.length;
    const completadas = e.items.filter(i => i.estado === 'Completado').length;
    const en_progreso = e.items.filter(i => i.estado === 'En Progreso').length;
    const pendientes = total - completadas - en_progreso;
    return {
      ...e,
      tareas: { total, completadas, en_progreso, pendientes }
    };
  });

  const totalTareasCount = proyecto.tareas_totales || (tareasProyecto.length || 18);
  const completadasCount = proyecto.tareas_completadas || tareasProyecto.filter(t => t.estado === 'COMPLETADO').length || 0;
  const revisionCount = tareasProyecto.filter(t => t.estado === 'EN_REVISION').length || 0;
  const progresoCount = tareasProyecto.filter(t => t.estado === 'EN_PROGRESO').length || 0;
  const pendientesCount = Math.max(0, totalTareasCount - completadasCount - revisionCount - progresoCount);

  return {
    ...proyecto,
    id_proyecto: proyecto.id_proyecto,
    nombre_proyecto: proyecto.nombre_proyecto,
    descripcion: proyecto.descripcion,
    estado: proyecto.estado,
    fecha_inicio: proyecto.fecha_inicio,
    fecha_fin: proyecto.fecha_fin,
    fecha_inicio_real: proyecto.fecha_inicio,
    fecha_fin_real: proyecto.estado === 'COMPLETADO' ? proyecto.fecha_fin : null,
    current_etapa_id: 2,
    colaboradores,
    etapas,
    metricas: {
      tareas_totales: totalTareasCount,
      tareas_en_revision: revisionCount,
      tareas_completadas: completadasCount,
      tareas_pendientes: pendientesCount,
      tareas_canceladas: 0,
      colaboradores_count: colaboradores.length
    }
  };
}

// Rutas de la API mock
const API_ROUTES = {
  // ==================== ADMIN ENDPOINTS ====================
  '/api/admin/estadisticas/': async () => {
    checkAuth();
    await delay();
    return ADMIN_STATS;
  },

  '/api/admin/dashboard/': async () => {
    checkAuth();
    await delay();
    return {
      stats: ADMIN_STATS,
      actividad_reciente: ACTIVIDAD_RECIENTE.slice(0, 5),
      proyectos_activos: PROYECTOS.filter(p => p.estado === 'ACTIVO').slice(0, 4),
      tareas_pendientes_revision: TAREAS_JEFE.length,
      notificaciones: NOTIFICACIONES.filter(n => !n.leida).length
    };
  },

  '/api/admin/usuarios/': async () => {
    checkAuth();
    await delay();
    const usuariosConRol = USUARIOS_SISTEMA.map(u => ({
      ...u,
      rol_nombre: u.rol,
      id_rol: u.id_rol || (u.rol === 'ADMIN' ? 1 : u.rol === 'JEFE' ? 2 : 3)
    }));
    return { usuarios: usuariosConRol };
  },

  '/api/admin/usuarios/crear/': async (options) => {
    checkAuth();
    await delay();
    return successResponse('Usuario creado correctamente');
  },

  '/api/admin/usuarios/editar/': async () => {
    checkAuth();
    await delay();
    return successResponse('Usuario actualizado');
  },

  '/api/admin/usuarios/eliminar/': async () => {
    checkAuth();
    await delay();
    return successResponse('Usuario desactivado');
  },

  // Detalle de usuario específico (para GestionUsuarios)
  '/api/admin/usuarios/:id/detalle/': async (options, params) => {
    checkAuth();
    await delay();
    const id = parseInt(params?.id || 1);
    const usuario = USUARIOS_SISTEMA.find(u => u.id_usuario === id) || USUARIOS_SISTEMA[0];
    return {
      usuario: {
        ...usuario,
        rol_nombre: usuario.rol,
        telefono_emergencia: '+56 9 9999 0000',
        tarifa: TARIFAS.find(t => 
          (usuario.rol === 'COLABORADOR' && t.nombre_tarifa === 'Senior') ||
          t.nombre_tarifa === 'Lead'
        ) || TARIFAS[2]
      }
    };
  },

  '/api/admin/proyectos/': async () => {
    checkAuth();
    await delay();
    const proyectosAdmin = PROYECTOS.map(p => ({
      ...p,
      jefe_nombre: p.jefe_nombre || p.jefe_proyecto?.nombre || 'Sin asignar',
      jefe_id: p.jefe_id || p.jefe_proyecto?.id || null,
      id_usuario: p.id_usuario || p.jefe_proyecto?.id || null
    }));
    return { proyectos: proyectosAdmin };
  },

  '/api/admin/proyectos/crear/': async () => {
    checkAuth();
    await delay();
    return successResponse('Proyecto creado');
  },

  '/api/admin/pagos/': async () => {
    checkAuth();
    await delay();
    const pendientes = PAGOS.filter(p => p.estado === 'PENDIENTE');
    const pagados = PAGOS.filter(p => p.estado === 'PAGADO');
    return { 
      pagos: PAGOS,
      resumen: {
        pendientes: pendientes.length,
        pagados: pagados.length,
        monto_pendiente: pendientes.reduce((s, p) => s + p.monto, 0),
        monto_pagado: pagados.reduce((s, p) => s + p.monto, 0)
      }
    };
  },

  '/api/admin/pagos/enviar/': async () => {
    checkAuth();
    await delay();
    return successResponse('Pagos procesados correctamente');
  },

  '/api/admin/pagos/marcar-pagado/': async () => {
    checkAuth();
    await delay();
    return successResponse('Pago marcado como realizado');
  },

  '/api/admin/suscripcion/': async () => {
    checkAuth();
    await delay();
    // Estructura completa para AdminSuscripcion.js
    const hoy = new Date();
    const fechaFin = new Date('2026-12-31');
    const diasRestantes = Math.ceil((fechaFin - hoy) / (1000 * 60 * 60 * 24));
    const diasTranscurridos = 365 - diasRestantes;
    
    return {
      suscripcion: {
        estado: SUSCRIPCION.estado,
        plan_nombre: SUSCRIPCION.plan,
        monto_anual: SUSCRIPCION.precio_anual,
        moneda: 'CLP',
        fecha_proximo_pago: SUSCRIPCION.fecha_proximo_cobro,
        periodo_fin: SUSCRIPCION.fecha_fin,
        dias_transcurridos: diasTranscurridos,
        dias_restantes: diasRestantes,
        usuarios_max: SUSCRIPCION.usuarios_max,
        usuarios_actual: SUSCRIPCION.usuarios_actual,
        proyectos_max: SUSCRIPCION.proyectos_max,
        proyectos_actual: SUSCRIPCION.proyectos_actual,
        caracteristicas: SUSCRIPCION.caracteristicas
      }
    };
  },

  '/api/admin/suscripcion/pagos/': async () => {
    checkAuth();
    await delay();
    // Estructura para AdminSuscripcion.js
    return { 
      pagos: SUSCRIPCION.historial_pagos.map(p => ({
        id_pago: p.id,
        mp_payment_id: `MP${p.id}${Date.now()}`,
        preference_id: `PREF_${p.id}`,
        estado_pago: p.estado,
        monto: p.monto,
        moneda: 'CLP',
        fecha_pagado: p.fecha
      }))
    };
  },

  '/api/admin/suscripcion/checkout/': async () => {
    checkAuth();
    await delay();
    return { init_point: '#demo-checkout', url: '#demo-checkout', message: 'Checkout demo' };
  },

  '/api/admin/configuracion/': async () => {
    checkAuth();
    await delay();
    return CONFIGURACION_EMPRESA;
  },

  '/api/admin/configuracion/actualizar/': async () => {
    checkAuth();
    await delay();
    return successResponse('Configuración actualizada');
  },

  '/api/admin/tarifas/': async () => {
    checkAuth();
    await delay();
    return { tarifas: TARIFAS };
  },

  '/api/admin/tarifas/crear/': async () => {
    checkAuth();
    await delay();
    return successResponse('Tarifa creada');
  },

  '/api/admin/tarifas/editar/': async () => {
    checkAuth();
    await delay();
    return successResponse('Tarifa actualizada');
  },

  '/api/admin/tarifas/eliminar/': async () => {
    checkAuth();
    await delay();
    return successResponse('Tarifa eliminada');
  },

  '/api/admin/actividad/': async () => {
    checkAuth();
    await delay();
    return { actividad: ACTIVIDAD_RECIENTE };
  },

  '/api/admin/notificaciones/': async () => {
    checkAuth();
    await delay();
    return { notificaciones: NOTIFICACIONES };
  },

  '/api/admin/reportes/general/': async () => {
    checkAuth();
    await delay();
    return {
      periodo: 'Abril 2026',
      proyectos: {
        total: PROYECTOS.length,
        activos: PROYECTOS.filter(p => p.estado === 'ACTIVO').length,
        completados: PROYECTOS.filter(p => p.estado === 'COMPLETADO').length
      },
      tareas: {
        total: TAREAS.length,
        completadas: TAREAS.filter(t => t.estado === 'COMPLETADO').length,
        en_progreso: TAREAS.filter(t => t.estado === 'EN_PROGRESO').length
      },
      pagos: {
        total_pagado: PAGOS.filter(p => p.estado === 'PAGADO').reduce((s, p) => s + p.monto, 0),
        pendiente: PAGOS.filter(p => p.estado === 'PENDIENTE').reduce((s, p) => s + p.monto, 0)
      },
      horas_trabajadas: 1056
    };
  },

  '/api/admin/reportes/enviar-pdf/': async () => {
    checkAuth();
    await delay();
    return successResponse('Reporte enviado por email');
  },

  '/api/admin/plantillas/general/': async () => {
    checkAuth();
    await delay();
    return { plantilla: null };
  },

  '/api/admin/plantillas/admin/': async () => {
    checkAuth();
    await delay();
    return { plantilla: null };
  },

  '/api/admin/plantillas/colaborador/': async () => {
    checkAuth();
    await delay();
    return { plantilla: null };
  },

  // ==================== JEFE ENDPOINTS ====================
  '/api/jefe/dashboard/': async () => {
    checkAuth();
    await delay();
    const misProyectos = PROYECTOS.filter(p => p.jefe_proyecto?.id === 2);
    return {
      proyectos_activos: misProyectos.filter(p => p.estado === 'ACTIVO').length,
      tareas_revision: TAREAS_JEFE.length,
      colaboradores_activos: 8,
      proyectos: misProyectos.slice(0, 4),
      tareas_pendientes: TAREAS_JEFE,
      actividad: ACTIVIDAD_RECIENTE.slice(0, 4)
    };
  },

  '/api/jefe/proyectos/': async () => {
    checkAuth();
    await delay();
    const proyectosJefe = PROYECTOS.filter(p => p.estado !== 'CANCELADO').map(p => ({
      ...p,
      colaboradores_activos: Array.isArray(p.colaboradores) ? p.colaboradores.length : 0
    }));
    return { proyectos: proyectosJefe };
  },

  // Aceptar proyecto con ID dinámico
  '/api/jefe/proyectos/:id/aceptar/': async (options, params) => {
    checkAuth();
    await delay();
    return successResponse('Proyecto aceptado');
  },

  // Rechazar proyecto con ID dinámico
  '/api/jefe/proyectos/:id/rechazar/': async (options, params) => {
    checkAuth();
    await delay();
    return successResponse('Proyecto rechazado');
  },

  '/api/jefe/proyectos/detalle/': async (options, params) => {
    checkAuth();
    await delay();
    const id = parseInt(params?.id || 1);
    return getProyectoDetalleMock(id);
  },

  // Detalle de proyecto con ID dinámico
  '/api/jefe/proyectos/:id/detalle/': async (options, params) => {
    checkAuth();
    await delay();
    const id = parseInt(params?.id || 1);
    return getProyectoDetalleMock(id);
  },

  '/api/jefe/proyectos/crear/': async () => {
    checkAuth();
    await delay();
    return successResponse('Proyecto creado');
  },

  '/api/jefe/tareas/': async () => {
    checkAuth();
    await delay();
    return { tareas: TAREAS_JEFE };
  },

  '/api/jefe/tareas/crear/': async () => {
    checkAuth();
    await delay();
    return successResponse('Tarea creada y asignada');
  },

  '/api/jefe/tareas/aprobar/': async () => {
    checkAuth();
    await delay();
    return successResponse('Tarea aprobada');
  },

  '/api/jefe/tareas/rechazar/': async () => {
    checkAuth();
    await delay();
    return successResponse('Tarea rechazada con comentarios');
  },

  '/api/jefe/invitaciones/': async () => {
    checkAuth();
    await delay();
    return { invitaciones: INVITACIONES_JEFE };
  },

  '/api/jefe/invitaciones/aceptar/': async () => {
    checkAuth();
    await delay();
    return successResponse('Invitación aceptada');
  },

  '/api/jefe/invitaciones/rechazar/': async () => {
    checkAuth();
    await delay();
    return successResponse('Invitación rechazada');
  },

  '/api/jefe/solicitudes-plazo/': async () => {
    checkAuth();
    await delay();
    return { solicitudes: [
      { 
        id: 1, 
        tarea: 'Integrar pasarela de pagos', 
        colaborador: 'Juan Pérez', 
        fecha_actual: '2026-04-15', 
        fecha_solicitada: '2026-04-25', 
        motivo: 'Complejidad adicional en integración con Transbank', 
        estado: 'PENDIENTE',
        fecha_respuesta: null,
        comentario_jefe: null
      },
      { 
        id: 2, 
        tarea: 'Testing QA completo', 
        colaborador: 'Sofía Martínez', 
        fecha_actual: '2026-04-20', 
        fecha_solicitada: '2026-04-28', 
        motivo: 'Falta de dispositivos de prueba', 
        estado: 'PENDIENTE',
        fecha_respuesta: null,
        comentario_jefe: null
      }
    ]};
  },

  '/api/jefe/solicitudes-plazo/aprobar/': async () => {
    checkAuth();
    await delay();
    return successResponse('Extensión de plazo aprobada');
  },

  '/api/jefe/revisiones/': async () => {
    checkAuth();
    await delay();
    return { revisiones: TAREAS_JEFE };
  },

  '/api/jefe/colaboradores/': async () => {
    checkAuth();
    await delay();
    const colaboradores = USUARIOS_SISTEMA.filter(u => u.rol === 'COLABORADOR' && u.estado === 'ACTIVO');
    return { colaboradores };
  },

  '/api/jefe/colaboradores/invitar/': async () => {
    checkAuth();
    await delay();
    return successResponse('Invitación enviada al colaborador');
  },

  '/api/jefe/estadisticas/': async () => {
    checkAuth();
    await delay();
    return {
      proyectos_activos: 4,
      tareas_totales: 35,
      tareas_completadas: 22,
      tareas_revision: 2,
      colaboradores: 8,
      horas_mes: 320
    };
  },

  // ==================== COLABORADOR ENDPOINTS ====================
  '/api/colaborador/dashboard/': async () => {
    const user = checkAuth();
    await delay();
    const misTareas = TAREAS.filter(t => t.asignado_a === user.id_usuario);
    return {
      tareas_pendientes: misTareas.filter(t => t.estado === 'PENDIENTE' || t.estado === 'EN_PROGRESO').length,
      tareas_completadas: misTareas.filter(t => t.estado === 'COMPLETADO' || t.estado === 'APROBADO').length,
      tareas_revision: misTareas.filter(t => t.estado === 'EN_REVISION').length,
      proyectos_activos: PROYECTOS_COLABORADOR.aceptados.filter(p => p.estado === 'ACTIVO').length,
      tareas: misTareas.slice(0, 5),
      proyectos: PROYECTOS_COLABORADOR.aceptados.slice(0, 4),
      horas_mes: 86,
      ingresos_mes: 1548000
    };
  },

  '/api/mis-tareas/': async () => {
    const user = checkAuth();
    await delay();
    const userId = user?.id_usuario || 3;
    let misTareas = TAREAS.filter(t => t.asignado_a === userId);
    if (misTareas.length === 0) {
      misTareas = TAREAS.filter(t => t.asignado_a === 3);
    }
    return { 
      tareas: misTareas, 
      rechazadas_count: misTareas.filter(t => t.estado === 'RECHAZADO').length 
    };
  },

  '/api/colaborador/tareas/': async () => {
    const user = checkAuth();
    await delay();
    const userId = user?.id_usuario || 3;
    let misTareas = TAREAS.filter(t => t.asignado_a === userId);
    if (misTareas.length === 0) {
      misTareas = TAREAS.filter(t => t.asignado_a === 3);
    }
    return { tareas: misTareas };
  },

  '/api/colaborador/tareas/detalle/': async (options, params) => {
    checkAuth();
    await delay();
    const id = parseInt(params?.id || 1);
    const tarea = TAREAS.find(t => t.id === id) || TAREAS[0];
    return { tarea };
  },

  '/api/colaborador/mis-proyectos/': async () => {
    checkAuth();
    await delay();
    return PROYECTOS_COLABORADOR;
  },

  '/api/colaborador/proyectos/': async () => {
    checkAuth();
    await delay();
    return { proyectos: PROYECTOS_COLABORADOR.aceptados };
  },

  // Detalle completo de proyecto para ColaboradorProyectoDetalle.js
  '/api/colaborador/proyectos/:id/detalle/': async (options, params) => {
    checkAuth();
    await delay();
    const id = parseInt(params?.id || 1);
    const proyecto = PROYECTOS.find(p => p.id_proyecto === id) || PROYECTOS[0];
    const tareasProyecto = TAREAS.filter(t => t.id_proyecto === id);
    
    // Generar métricas
    const metricas = {
      tareas_totales: tareasProyecto.length,
      tareas_en_revision: tareasProyecto.filter(t => t.estado === 'EN_REVISION').length,
      tareas_completadas: tareasProyecto.filter(t => t.estado === 'COMPLETADO' || t.estado === 'APROBADO').length,
      tareas_pendientes: tareasProyecto.filter(t => t.estado === 'PENDIENTE').length,
      tareas_canceladas: 0,
      colaboradores_count: proyecto.colaboradores?.length || 0
    };
    
    // Generar etapas con tareas
    const etapas = [
      {
        id_etapa: 1,
        nombre: 'Análisis',
        slug: 'analisis',
        orden: 1,
        estado: 'COMPLETADO',
        tareas: { total: 2, pendientes: 0, en_progreso: 0, completadas: 2 },
        items: tareasProyecto.filter(t => t.nombre.toLowerCase().includes('análisis') || t.nombre.toLowerCase().includes('diseño')).slice(0, 2).map(t => ({
          id_tarea: t.id,
          nombre: t.nombre,
          estado: t.estado,
          prioridad: t.prioridad,
          colaborador: t.colaborador?.nombre || 'Sin asignar',
          fecha_inicio: t.fecha_creacion,
          fecha_entrega_inicial: t.fecha_entrega,
          fecha_termino: t.fecha_completado || null
        }))
      },
      {
        id_etapa: 2,
        nombre: 'Desarrollo',
        slug: 'desarrollo',
        orden: 2,
        estado: 'EN_PROGRESO',
        tareas: { total: tareasProyecto.length - 2, pendientes: metricas.tareas_pendientes, en_progreso: tareasProyecto.filter(t => t.estado === 'EN_PROGRESO').length, completadas: metricas.tareas_completadas - 2 },
        items: tareasProyecto.slice(2).map(t => ({
          id_tarea: t.id,
          nombre: t.nombre,
          estado: t.estado,
          prioridad: t.prioridad,
          colaborador: t.colaborador?.nombre || 'Sin asignar',
          fecha_inicio: t.fecha_creacion,
          fecha_entrega_inicial: t.fecha_entrega,
          fecha_termino: t.fecha_completado || null
        }))
      },
      {
        id_etapa: 3,
        nombre: 'Testing',
        slug: 'testing',
        orden: 3,
        estado: 'PENDIENTE',
        tareas: { total: 0, pendientes: 0, en_progreso: 0, completadas: 0 },
        items: []
      }
    ];
    
    // Colaboradores del proyecto
    const colaboradores = (proyecto.colaboradores || []).map(colabId => {
      const user = USUARIOS_SISTEMA.find(u => u.id_usuario === colabId);
      return user ? {
        id_usuario: user.id_usuario,
        nombre: user.nombre,
        email: user.email,
        estado: user.estado
      } : null;
    }).filter(Boolean);
    
    return {
      nombre_proyecto: proyecto.nombre_proyecto,
      descripcion: proyecto.descripcion,
      estado: proyecto.estado,
      fecha_inicio: proyecto.fecha_inicio,
      fecha_inicio_real: proyecto.fecha_inicio,
      fecha_fin: proyecto.fecha_fin,
      fecha_fin_real: null,
      colaboradores,
      metricas,
      etapas
    };
  },

  '/api/colaborador/invitaciones/': async () => {
    checkAuth();
    await delay();
    return { 
      solicitudes: PROYECTOS_COLABORADOR.solicitudes, 
      aceptadas: PROYECTOS_COLABORADOR.aceptados 
    };
  },

  '/api/colaborador/invitaciones/aceptar/': async () => {
    checkAuth();
    await delay();
    return successResponse('Te has unido al proyecto');
  },

  '/api/colaborador/invitaciones/rechazar/': async () => {
    checkAuth();
    await delay();
    return successResponse('Invitación rechazada');
  },

  '/api/colaborador/solicitudes-plazo/': async () => {
    checkAuth();
    await delay();
    return {
      solicitudes: [
        {
          id: 1,
          id_tarea: 4,
          tarea_nombre: 'Sistema de gestión de inventario',
          proyecto_nombre: 'Portal E-Commerce Premium',
          fecha_solicitud: '2026-03-25',
          fecha_actual: '2026-04-01',
          nueva_fecha_solicitada: '2026-04-10',
          motivo: 'Integración tardía con API del proveedor',
          estado: 'APROBADO',
          comentario_jefe: 'Aprobado. Extensión concedida hasta el 10 de Abril.',
          fecha_respuesta: '2026-03-26'
        }
      ]
    };
  },

  '/api/colaborador/solicitar-plazo/': async () => {
    checkAuth();
    await delay();
    return successResponse('Solicitud de extensión enviada');
  },

  '/api/colaborador/solicitudes-tarea/': async () => {
    checkAuth();
    await delay();
    return {
      solicitudes: [
        {
          id: 1,
          id_tarea: 17,
          tarea_nombre: 'Módulo de Autenticación OAuth2',
          proyecto_nombre: 'Portal E-Commerce Premium',
          fecha_solicitud: '2026-04-02',
          prioridad: 'ALTA',
          estado: 'PENDIENTE'
        },
        {
          id: 2,
          id_tarea: 18,
          tarea_nombre: 'Integración Pasarela de Pagos Webpay',
          proyecto_nombre: 'Portal E-Commerce Premium',
          fecha_solicitud: '2026-04-04',
          prioridad: 'ALTA',
          estado: 'PENDIENTE'
        }
      ]
    };
  },

  '/api/colaborador/mis-pagos/': async () => {
    const user = checkAuth();
    await delay();
    const userId = user?.id_usuario || 3;
    let misPagos = PAGOS.filter(p => p.id_colaborador === userId);
    if (misPagos.length === 0) {
      misPagos = PAGOS.filter(p => p.id_colaborador === 3);
    }
    const pagosFormateados = misPagos.map(p => ({
      ...p,
      mes: p.periodo.split(' ')[0],
      anio: parseInt(p.periodo.split(' ')[1]) || 2026,
      monto_bruto: p.monto,
      retencion: Math.round(p.monto * 0.145),
      monto_liquido: Math.round(p.monto * 0.855),
      creado_en: p.fecha
    }));
    return { 
      pagos: pagosFormateados,
      resumen: {
        total_recibido: pagosFormateados.filter(p => p.estado === 'PAGADO').reduce((s, p) => s + p.monto_liquido, 0),
        pendiente: pagosFormateados.filter(p => p.estado === 'PENDIENTE').reduce((s, p) => s + p.monto_liquido, 0)
      }
    };
  },

  '/api/colaborador/pagos/': async () => {
    const user = checkAuth();
    await delay();
    const userId = user?.id_usuario || 3;
    let misPagos = PAGOS.filter(p => p.id_colaborador === userId);
    if (misPagos.length === 0) {
      misPagos = PAGOS.filter(p => p.id_colaborador === 3);
    }
    const pagosFormateados = misPagos.map(p => ({
      ...p,
      mes: p.periodo.split(' ')[0],
      anio: parseInt(p.periodo.split(' ')[1]) || 2026,
      monto_bruto: p.monto,
      retencion: Math.round(p.monto * 0.145),
      monto_liquido: Math.round(p.monto * 0.855),
      creado_en: p.fecha
    }));
    return { pagos: pagosFormateados };
  },

  '/api/colaborador/tareas/aceptar/': async () => {
    checkAuth();
    await delay();
    return successResponse('Tarea aceptada');
  },

  '/api/colaborador/tareas/rechazar/': async () => {
    checkAuth();
    await delay();
    return successResponse('Tarea rechazada');
  },

  '/api/colaborador/tareas/entregar/': async () => {
    checkAuth();
    await delay();
    return successResponse('Tarea enviada a revisión');
  },

  '/api/colaborador/tareas/iniciar/': async () => {
    checkAuth();
    await delay();
    return successResponse('Tarea iniciada');
  },

  '/api/colaborador/tarifa-asignada/': async () => {
    checkAuth();
    await delay();
    return { tarifa: TARIFAS[2] }; // Senior
  },

  '/api/colaborador/tarifa/': async () => {
    checkAuth();
    await delay();
    return { tarifa: 18000, moneda: 'CLP', nombre: 'Senior' };
  },

  '/api/colaborador/estadisticas/': async () => {
    const user = checkAuth();
    await delay();
    const misTareas = TAREAS.filter(t => t.asignado_a === user.id_usuario);
    return {
      totalTareas: misTareas.length,
      tareasCompletadas: misTareas.filter(t => t.estado === 'COMPLETADO' || t.estado === 'APROBADO').length,
      tareasPendientes: misTareas.filter(t => t.estado === 'PENDIENTE').length,
      tareasEnProgreso: misTareas.filter(t => t.estado === 'EN_PROGRESO').length,
      tareasEnRevision: misTareas.filter(t => t.estado === 'EN_REVISION').length,
      totalProyectos: PROYECTOS_COLABORADOR.aceptados.length,
      horasTrabajadas: 86,
      ingresosMes: 1548000
    };
  },

  // ==================== ENTREGAS Y ARCHIVOS ====================
  '/api/entregas/subir/': async () => {
    checkAuth();
    await delay();
    return successResponse('Archivo subido correctamente');
  },

  '/api/entregas/': async () => {
    checkAuth();
    await delay();
    return { entregas: [
      { id: 1, tarea_id: 4, archivo: 'inventario_v1.zip', fecha: '2026-03-28', tamano: '2.4 MB' },
      { id: 2, tarea_id: 12, archivo: 'cicd_config.zip', fecha: '2026-03-14', tamano: '124 KB' }
    ]};
  },

  '/api/solicitar-plazo/': async () => {
    checkAuth();
    await delay();
    return successResponse('Solicitud de plazo enviada');
  },

  // ==================== PERFIL ====================
  '/api/perfil/': async () => {
    const user = checkAuth();
    await delay();
    return user;
  },

  '/api/usuario/perfil/': async () => {
    const user = checkAuth();
    await delay();
    return user;
  },

  '/api/perfil/actualizar/': async () => {
    checkAuth();
    await delay();
    return successResponse('Perfil actualizado');
  },

  '/api/perfil/cambiar-password/': async () => {
    checkAuth();
    await delay();
    return successResponse('Contraseña actualizada');
  },

  // ==================== REPORTES ====================
  '/api/reportes/pagos/': async () => {
    checkAuth();
    await delay();
    return { 
      pagos: PAGOS, 
      total: PAGOS.reduce((s, p) => s + p.monto, 0),
      por_estado: {
        pendiente: PAGOS.filter(p => p.estado === 'PENDIENTE').reduce((s, p) => s + p.monto, 0),
        pagado: PAGOS.filter(p => p.estado === 'PAGADO').reduce((s, p) => s + p.monto, 0)
      }
    };
  },

  '/api/reportes/proyectos/': async () => {
    checkAuth();
    await delay();
    return { 
      proyectos: PROYECTOS, 
      resumen: {
        activos: PROYECTOS.filter(p => p.estado === 'ACTIVO').length,
        completados: PROYECTOS.filter(p => p.estado === 'COMPLETADO').length,
        pendientes: PROYECTOS.filter(p => p.estado === 'PENDIENTE').length,
        total: PROYECTOS.length
      }
    };
  },

  '/api/reportes/tareas/': async () => {
    checkAuth();
    await delay();
    return {
      tareas: TAREAS,
      resumen: {
        completadas: TAREAS.filter(t => t.estado === 'COMPLETADO').length,
        en_progreso: TAREAS.filter(t => t.estado === 'EN_PROGRESO').length,
        pendientes: TAREAS.filter(t => t.estado === 'PENDIENTE').length,
        revision: TAREAS.filter(t => t.estado === 'EN_REVISION').length
      }
    };
  },

  '/api/reportes/usuarios/': async () => {
    checkAuth();
    await delay();
    return {
      usuarios: USUARIOS_SISTEMA,
      resumen: {
        total: USUARIOS_SISTEMA.length,
        activos: USUARIOS_SISTEMA.filter(u => u.estado === 'ACTIVO').length,
        inactivos: USUARIOS_SISTEMA.filter(u => u.estado === 'INACTIVO').length,
        por_rol: {
          admin: USUARIOS_SISTEMA.filter(u => u.rol === 'ADMIN').length,
          jefe: USUARIOS_SISTEMA.filter(u => u.rol === 'JEFE').length,
          colaborador: USUARIOS_SISTEMA.filter(u => u.rol === 'COLABORADOR').length
        }
      }
    };
  },

  // ==================== SUPERADMIN ====================
  '/api/superadmin/empresas/': async () => {
    checkAuth();
    await delay();
    return {
      empresas: [
        { id: 1, nombre: 'TechSolutions Chile SpA', plan: 'Profesional', usuarios: 15, proyectos: 6, estado: 'ACTIVA', fecha_registro: '2025-06-01' },
        { id: 2, nombre: 'Digital Partners Ltda.', plan: 'Básico', usuarios: 5, proyectos: 2, estado: 'ACTIVA', fecha_registro: '2025-08-15' },
        { id: 3, nombre: 'Innovación SpA', plan: 'Enterprise', usuarios: 48, proyectos: 15, estado: 'ACTIVA', fecha_registro: '2025-03-10' },
        { id: 4, nombre: 'StartupX', plan: 'Básico', usuarios: 3, proyectos: 1, estado: 'TRIAL', fecha_registro: '2026-03-20' }
      ]
    };
  },

  '/api/superadmin/empresas/crear/': async () => {
    checkAuth();
    await delay();
    return successResponse('Empresa creada');
  },

  '/api/superadmin/estadisticas/': async () => {
    checkAuth();
    await delay();
    return {
      totalEmpresas: 4,
      empresasActivas: 3,
      empresasTrial: 1,
      totalUsuarios: 71,
      ingresosMensuales: 650000,
      proyectosActivos: 24
    };
  },

  // ==================== AUTH ====================
  '/api/auth/reset-password/send/': async () => {
    await delay();
    return successResponse('Email de recuperación enviado');
  },

  '/api/auth/reset-password/': async () => {
    await delay();
    return successResponse('Contraseña actualizada');
  },

  // ==================== GENERICOS ====================
  '/api/usuarios/': async () => {
    checkAuth();
    await delay();
    return { usuarios: USUARIOS_SISTEMA };
  },

  '/api/proyectos/': async () => {
    checkAuth();
    await delay();
    return { proyectos: PROYECTOS };
  },

  '/api/tareas/': async () => {
    checkAuth();
    await delay();
    return { tareas: TAREAS };
  },

  '/api/tarifas/': async () => {
    checkAuth();
    await delay();
    return { tarifas: TARIFAS };
  },

  '/api/configuracion/': async () => {
    checkAuth();
    await delay();
    return CONFIGURACION_EMPRESA;
  },

  '/api/notificaciones/': async () => {
    checkAuth();
    await delay();
    return { notificaciones: NOTIFICACIONES };
  },

  '/api/notificaciones/marcar-leida/': async () => {
    checkAuth();
    await delay();
    return successResponse('Notificación marcada como leída');
  }
};

// Función para hacer match de rutas con parámetros
function matchRoute(url) {
  let cleanUrl = url.split('?')[0];
  if (!cleanUrl.endsWith('/')) cleanUrl += '/';
  
  // Coincidencia exacta
  if (API_ROUTES[cleanUrl]) {
    return { handler: API_ROUTES[cleanUrl], params: {} };
  }

  // Rutas con parámetros numéricos (ej: /api/proyectos/1/)
  const urlParts = cleanUrl.split('/').filter(Boolean);
  for (const [route, handler] of Object.entries(API_ROUTES)) {
    const routeParts = route.split('/').filter(Boolean);
    if (routeParts.length !== urlParts.length) continue;

    const params = {};
    let match = true;

    for (let i = 0; i < routeParts.length; i++) {
      if (routeParts[i].startsWith(':')) {
        params[routeParts[i].slice(1)] = urlParts[i];
      } else if (routeParts[i] !== urlParts[i]) {
        match = false;
        break;
      }
    }

    if (match) return { handler, params };
  }

  return null;
}

// Handler principal de API mock
export async function mockFetch(url, options = {}) {
  const route = matchRoute(url);

  if (!route) {
    await delay(150);
    return { data: [], success: true, message: 'Endpoint demo no implementado', demo: true };
  }

  try {
    const result = await route.handler(options, route.params);
    return result;
  } catch (error) {
    throw error;
  }
}

// Verificar si una URL debe usar mock
export function shouldUseMock(url) {
  return url.includes('/api/');
}
